import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { resolveId } from '@wix/communities-blog-client-common';
import MoreIcon from '@app/external/common/components/icons/more-icon';
import { PinIcon } from '@app/external/common/components/icons/pin-icon';
import { useGetContainerPosition } from '@app/external/common/components/more-button/more-button-hook';
import { useMenuCloseState } from '@app/external/common/components/more-button/use-menu-close-state';
import PopoverRoot from '@app/external/common/components/popovers/popover-root';
import { usePostActions } from '@app/external/common/components/post-actions/post-actions';
import { useActions, useSelector } from '@app/external/common/components/runtime-context';
import { isSite as getIsSite } from '@app/external/common/store/basic-params/basic-params-selectors';
import { type NormalizedPost } from '@app/external/common/types';
import { useFullPostLayoutOverrides } from './feed-overrides-context';
import styles from './post-page-post-header-more-actions.scss';

type PostPagePostHeaderMoreActionsProps = {
  post: NormalizedPost;
};

const PostPagePostHeaderMoreActions: React.FC<PostPagePostHeaderMoreActionsProps> = ({ post }) => {
  const overrides = useFullPostLayoutOverrides();

  return (
    <div className={styles.root}>
      {post.isPinned ? (
        <span className={styles.iconBox}>
          <PinIcon />
        </span>
      ) : undefined}

      {overrides.showMoreOptionsMenu && <PostPageMoreActionsMenu post={post} />}
    </div>
  );
};

type PostPageMoreActionsMenuProps = {
  post: NormalizedPost;
};

const PostPageMoreActionsMenu: React.FC<PostPageMoreActionsMenuProps> = ({ post }) => {
  const postId = resolveId(post);
  const htmlId = `more-button-${postId}` as const;
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const actionsContainerRef = useRef<HTMLDivElement | null>(null);

  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();
  const actions = useActions();

  const itemsToShow = usePostActions(post);
  const items = [itemsToShow.share, itemsToShow.print].filter(
    (f): f is NonNullable<typeof f> => f !== undefined,
  );

  const hideComponent = useCallback((): void => {
    setIsVisible(false);
  }, []);

  const showComponent = () => {
    actions.postPageMoreButtonClicked({ postId });
    setIsVisible(true);
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();

    if (isVisible) {
      hideComponent();
    } else {
      showComponent();
    }
  };

  useMenuCloseState({
    isOpen: isVisible,
    onClose: hideComponent,
    triggerRef: buttonRef,
    menuRef: actionsContainerRef,
  });

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      <button
        onClick={handleClick}
        className={styles.menuButton}
        ref={buttonRef}
        type="button"
        aria-expanded={isVisible}
        aria-haspopup="menu"
        aria-label={t('more-button.more-actions')}
        id={htmlId}
        data-hook="more-button"
      >
        <MoreIcon />
      </button>
      {isVisible
        ? ReactDOM.createPortal(
            <PostPageMoreActionsMenuItems
              items={items}
              buttonRef={buttonRef}
              actionsContainerRef={actionsContainerRef}
              onClose={hideComponent}
            />,
            PopoverRoot.getPopoverRootElement()!,
          )
        : null}
    </>
  );
};

type DefinedPostActionItem = NonNullable<
  ReturnType<typeof usePostActions>[keyof ReturnType<typeof usePostActions>]
>;

type PostPageMoreActionsMenuItemsProps = {
  items: DefinedPostActionItem[];
  buttonRef: React.RefObject<HTMLButtonElement>;
  actionsContainerRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
};

const PostPageMoreActionsMenuItems: React.FC<PostPageMoreActionsMenuItemsProps> = ({
  items,
  buttonRef,
  actionsContainerRef,
  onClose,
}) => {
  const actions = useActions();
  const isSite = useSelector(getIsSite);
  const componentId = useSelector((_, host) => host.id);
  const { calculateActionsContainerPosition, checkIfFits, checkIfFitsInEditor } =
    useGetContainerPosition({
      actionsContainerRef,
      containerRef: buttonRef,
      componentId,
    });

  useEffect(() => {
    actions.actionsOpened({ type: 'post' });

    return () => {
      actions.actionsClosed({ type: 'post' });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount/unmount only
  }, []);

  useEffect(() => {
    if (isSite) {
      checkIfFits();
    } else {
      checkIfFitsInEditor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const focusable = actionsContainerRef.current?.querySelector(
      'button, [href], [tabindex]:not([tabindex="-1"])',
    ) as HTMLElement | undefined;
    focusable?.focus();
  }, [actionsContainerRef]);

  return (
    <div
      ref={actionsContainerRef}
      className={styles.popoverRoot}
      style={calculateActionsContainerPosition()}
      role="menu"
      data-hook="actions"
      aria-labelledby={buttonRef.current?.id}
    >
      {items.map((item) => {
        const { onClick, ...restButtonProps } = item.buttonProps;

        return (
          <button
            key={item.buttonProps['data-hook']}
            role="menuitem"
            className={styles.listItem}
            aria-label={item.text}
            {...restButtonProps}
            onClick={() => {
              onClick?.();
              onClose();
            }}
          >
            <span className={styles.listItemIconWrapper}>
              <item.icon />
            </span>

            {item.text}
          </button>
        );
      })}
    </div>
  );
};

export default PostPagePostHeaderMoreActions;
